import { EntryCollection } from 'contentful';

import {
  CONTENT_GROUP_ACCOUNT_PAGE,
  CONTENT_TYPE_ACCOUNT_PAGE,
} from '@/core/constants';

import {
  TypeAccountPage,
  TypeKeyValueListFields,
  TypeProduct,
} from '@/core/generated-types';

import { filterContentfulObject } from '@/utils/helpers';

import { client, previewClient } from './contentfulClients';

import middleware from './middleware';

type GetEntriesConfig = {
  contentType?: string;
  include?: number;
  locale?: string;
  select?: string;
  [k: `fields.${string}`]: string | undefined;
};

export type InventoryStatusCopy = {
  availableCta: string;
  outOfStock: string;
  outOfStockCta: string;
  outOfStockTitle: string;
  outOfStockDescription: string;
  preSale: string;
  preSaleCta: string;
  backOrder: string;
  unavailableTitle: string;
  unavailableDescription: string;
  addedToOrder: string;
};

export type ProductVariantCopy = {
  [key: string]: string;
};

export type ProductPageGeneralCopy = {
  [key: string]: string;
};

export type ContentfulPreviewData = {
  env?: string;
};

export type ContentfulLinkExternalFields = {
  entryName: string;
  linkText: string;
  ariaLabel: string;
  hasArrow: boolean;
  linkUrl: string;
  blankTarget: boolean;
  noFollow: boolean;
  hasShopSubdomain: boolean;
};

export async function getEntries<T extends object>(
  config: GetEntriesConfig,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
  shouldSanitize = true,
): Promise<EntryCollection<T>> {
  const { contentType, include, locale, select, ...fields } = config;

  const contentQuery = {
    content_type: contentType,
    include: include || 10,
    locale: locale || 'en-US',
    ...(select && { select }),
    ...fields,
  };

  try {
    const entries = isPreview
      ? await previewClient(previewData?.env).getEntries<T>(contentQuery)
      : await client.getEntries<T>(contentQuery);

    if (shouldSanitize) {
      const sanitizedEntries = middleware(entries);

      return sanitizedEntries;
    } else {
      return entries;
    }
  } catch (error) {
    console.error(error);

    throw error;
  }
}

export function getEntriesByPageType<T extends Record<string, any>>(
  config: GetEntriesConfig,
  pageTypes: object,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
) {
  const { locale, ...fields } = config;

  const contentQuery = {
    locale: locale ?? '*',
    ...fields,
  };

  const promises: Promise<EntryCollection<T>>[] = [];

  for (const pageType of Object.values(pageTypes)) {
    promises.push(
      getEntries<T>(
        {
          ...contentQuery,
          contentType: pageType,
        },
        isPreview,
        previewData,
      ),
    );
  }

  return Promise.all(promises);
}

export async function getAccountPage(
  targetPageGroup: CONTENT_GROUP_ACCOUNT_PAGE,
  locale: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
) {
  const page = await getEntriesByPageType(
    {
      locale,
      'fields.contentGroup': targetPageGroup,
    },
    CONTENT_TYPE_ACCOUNT_PAGE,
    isPreview,
    previewData,
  );

  const queriedPages = page?.[0].items;

  const targetPage = queriedPages?.[0] as TypeAccountPage;

  return filterContentfulObject(targetPage);
}

export async function getKeyValueLists(
  ids: string[],
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
): Promise<object | null> {
  try {
    const contentQuery = {
      content_type: 'keyValueList',
      locale: locale || 'en-US',
      'fields.id[in]': ids.join(',') || '',
    };

    const entries: EntryCollection<TypeKeyValueListFields> = isPreview
      ? await previewClient(previewData?.env).getEntries(contentQuery)
      : await client.getEntries(contentQuery);

    if (!entries) {
      return null;
    }

    // There is more than likely a more performant and elegant way to achieve this using, for example, the .reduce() method.
    const mappedKeyValues: any = {};

    for (let i = 0; i < ids.length; i++) {
      const iteratedId = ids[i];

      mappedKeyValues[iteratedId] = null; // We initialize a null property for this ID.

      for (let j = 0; j < entries.items.length; j++) {
        const iteratedEntryFields = entries.items[j]?.fields;

        if (iteratedEntryFields?.id === iteratedId) {
          if (!mappedKeyValues[iteratedId]) {
            mappedKeyValues[iteratedId] = {}; // Since we have a match, make the property a nested object.
          }

          for (let k = 0; k < iteratedEntryFields?.keyValues.length; k++) {
            const keyValue = iteratedEntryFields?.keyValues[k];
            mappedKeyValues[iteratedId][keyValue.key] = keyValue.value;
          }
        }
      }
    }

    return mappedKeyValues;
  } catch (error) {
    console.error(error);

    return null;
  }
}

export async function getInventoryStatusCopy(
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
): Promise<InventoryStatusCopy | null> {
  const inventoryStatusCopy = (await getKeyValueLists(
    ['inventoryStatuses'],
    locale,
    isPreview,
    previewData,
  )) as any;

  return (inventoryStatusCopy?.inventoryStatuses ||
    null) as InventoryStatusCopy | null;
}

export async function getProductVariantsCopy(
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
) {
  const productVariantsCopy = (await getKeyValueLists(
    ['productVariantTranslations'],
    locale,
    isPreview,
    previewData,
  )) as any;

  return productVariantsCopy?.productVariantTranslations || null;
}

export async function getProductPageGeneralCopy(
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
) {
  const productPageGeneralCopy = (await getKeyValueLists(
    ['productPageTranslations'],
    locale,
    isPreview,
    previewData,
  )) as any;

  return productPageGeneralCopy?.productPageTranslations || null;
}

export const getProductByPlaygeniusProductAnchor = async (
  playgeniusProductAnchor: string,
  locale?: string,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
): Promise<TypeProduct> => {
  try {
    const contentQuery = {
      'fields.playgeniusProductAnchor': playgeniusProductAnchor,
      content_type: 'product',
      locale: locale || 'en-US',
    };

    const entries = isPreview
      ? await previewClient(previewData?.env).getEntries<TypeProduct>(
          contentQuery,
        )
      : await client.getEntries<TypeProduct>(contentQuery);

    return (entries.items[0] as unknown as TypeProduct) || null;
  } catch (error) {
    console.error(error);

    throw error;
  }
};

export const getContentfulObjectFromItems = <T extends { fields: any }>(
  items: T[],
  fieldsKey: keyof T['fields'],
) =>
  items.reduce(
    (acc, item) => {
      const key = item.fields[fieldsKey];

      return {
        ...acc,
        [key]: item,
      };
    },
    {} as Record<string, T>,
  );
